import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation } from 'react-query';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Badge, { BadgeVariants } from '@/components/layout/Badge';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Modal from '@/components/layout/Modal';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronUp, mdiLink, mdiLoading, mdiPlus, mdiSync, mdiTrashCan } from '@mdi/js';
import { toastError, toastSuccess } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';
import { queryClient } from '@/queryClient';
import { CategoryDTO, CategorySourceDTO, CategoryStatus } from '@webip/shared/src/dto/firewall';

type ResponseCategories = {
    ok?: boolean;
    result: CategoryDTO[];
};

const STATUS_VARIANT: Record<CategoryStatus, BadgeVariants> = {
    ok: 'success',
    error: 'danger',
    syncing: 'warning',
};

export function CategoriesTab() {
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [syncingId, setSyncingId] = useState<number | null>(null);
    const [isAddSourceOpen, setIsAddSourceOpen] = useState(false);
    const [addSourceCatId, setAddSourceCatId] = useState<number | null>(null);
    const [newSourceName, setNewSourceName] = useState('');
    const [newSourceUrl, setNewSourceUrl] = useState('');

    const queryCategories = useQuery({
        queryKey: ['fw_categories'],
        queryFn: async () =>
            axios.get<ResponseCategories>('/api/firewall/categories', { withCredentials: true }),
        refetchOnWindowFocus: false,
        refetchInterval: (data) => {
            const cats = data?.data?.result ?? [];
            return cats.some((c: CategoryDTO) => c.status === 'syncing') ? 3000 : false;
        },
    });

    const mutAddSource = useMutation(
        async ({ catId, name, url }: { catId: number; name: string; url: string }) =>
            axios.post(`/api/firewall/categories/${catId}/sources`, { name, url }, { withCredentials: true }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['fw_categories']);
                toastSuccess('Fonte adicionada com sucesso!');
                setIsAddSourceOpen(false);
                setNewSourceName('');
                setNewSourceUrl('');
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao adicionar fonte');
                toastError(message);
            },
        },
    );

    const mutToggleSource = useMutation(
        async ({ sourceId, active }: { sourceId: number; active: boolean }) =>
            axios.put(`/api/firewall/categories/sources/${sourceId}`, { active }, { withCredentials: true }),
        {
            onSuccess: () => queryClient.invalidateQueries(['fw_categories']),
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao atualizar fonte');
                toastError(message);
            },
        },
    );

    const mutDeleteSource = useMutation(
        async (sourceId: number) =>
            axios.delete(`/api/firewall/categories/sources/${sourceId}`, { withCredentials: true }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['fw_categories']);
                toastSuccess('Fonte removida!');
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao remover fonte');
                toastError(message);
            },
        },
    );

    const handleSync = useCallback(async (id: number) => {
        if (syncingId) return;
        setSyncingId(id);
        try {
            await axios.post(`/api/firewall/categories/${id}/sync`, {}, { withCredentials: true });
            queryClient.invalidateQueries(['fw_categories']);
            toastSuccess('Sincronização iniciada!');
        } catch (error) {
            const { message } = extractApiError(error, 'Erro ao sincronizar');
            toastError(message);
        } finally {
            setSyncingId(null);
        }
    }, [syncingId]);

    const openAddSource = (catId: number) => {
        setAddSourceCatId(catId);
        setNewSourceName('');
        setNewSourceUrl('');
        setIsAddSourceOpen(true);
    };

    const categories = queryCategories.data?.data?.result ?? [];

    const headers: DataTableHeader[] = [
        {
            label: '',
            width: '40px',
            render: (row: CategoryDTO) => (
                <Botao
                    size="sm"
                    variant="outline-secondary"
                    icon={expandedId === row.id ? mdiChevronUp : mdiChevronDown}
                    onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                    title="Expandir fontes"
                />
            ),
        },
        {
            label: 'Categoria',
            column: 'name',
            width: '20%',
            render: (row: CategoryDTO) => (
                <VBox>
                    <HBox style={{ gap: '8px', alignItems: 'center' }}>
                        <strong>{row.name}</strong>
                        {row.builtin && <Badge variant="info">Sistema</Badge>}
                    </HBox>
                    {row.description && (
                        <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
                            {row.description}
                        </span>
                    )}
                </VBox>
            ),
        },
        {
            label: 'Fontes',
            width: '80px',
            alignCenter: true,
            render: (row: CategoryDTO) => (
                <HBox style={{ gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon path={mdiLink} size={0.6} />
                    {row.sources.length}
                </HBox>
            ),
        },
        {
            label: 'Entradas',
            width: '100px',
            alignCenter: true,
            render: (row: CategoryDTO) => row.entries.toLocaleString('pt-BR'),
        },
        {
            label: 'Tipo',
            width: '80px',
            alignCenter: true,
            render: (row: CategoryDTO) => row.entryType,
        },
        {
            label: 'Status',
            width: '90px',
            alignCenter: true,
            render: (row: CategoryDTO) => (
                <Badge variant={STATUS_VARIANT[row.status]}>{row.status}</Badge>
            ),
        },
        {
            label: 'Último sync',
            width: '150px',
            render: (row: CategoryDTO) =>
                row.lastSync
                    ? new Date(row.lastSync).toLocaleString('pt-BR')
                    : '—',
        },
        {
            label: 'Ações',
            width: '90px',
            stickRight: true,
            alignRight: true,
            render: (row: CategoryDTO) => (
                <HBox style={{ gap: '8px', justifyContent: 'flex-end' }}>
                    <Botao
                        size="sm"
                        variant="outline-success"
                        icon={syncingId === row.id ? mdiLoading : mdiSync}
                        iconProps={{ spin: syncingId === row.id }}
                        disabled={syncingId === row.id || row.status === 'syncing' || row.sources.length === 0}
                        onClick={() => handleSync(row.id)}
                        title={row.sources.length === 0 ? 'Adicione fontes para sincronizar' : 'Sincronizar categoria'}
                    />
                </HBox>
            ),
        },
    ];

    return (
        <VBox style={{ gap: '16px', flex: 1, overflow: 'hidden' }}>
            <VBox style={{ padding: '8px 0' }}>
                <span style={{ color: 'var(--color-muted)', fontSize: '13px' }}>
                    Categorias pré-definidas pelo sistema com fontes de listas conhecidas da Internet.
                    Adicione ou remova fontes para ajustar o conteúdo de cada categoria.
                </span>
            </VBox>

            {queryCategories.isLoading && (
                <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                    <Icon path={mdiLoading} spin size={2} />
                </HBox>
            )}

            {queryCategories.isError && (
                <p style={{ color: 'var(--status-offline)' }}>Erro ao carregar categorias. Tente novamente.</p>
            )}

            {!queryCategories.isLoading && !queryCategories.isError && (
                <VBox style={{ gap: '0' }}>
                    <DataTable
                        cabecalhos={headers}
                        linhas={categories}
                    />
                    {/* Expanded source panel */}
                    {expandedId && (() => {
                        const cat = categories.find((c) => c.id === expandedId);
                        if (!cat) return null;
                        return (
                            <VBox
                                style={{
                                    background: 'var(--bg-3)',
                                    border: '1px solid var(--border-1)',
                                    borderTop: 'none',
                                    borderRadius: '0 0 6px 6px',
                                    padding: '16px',
                                    gap: '12px',
                                }}
                            >
                                <HBox style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                    <strong>Fontes de "{cat.name}"</strong>
                                    <Botao
                                        size="sm"
                                        variant="outline-primary"
                                        icon={mdiPlus}
                                        onClick={() => openAddSource(cat.id)}
                                    >
                                        Adicionar Fonte
                                    </Botao>
                                </HBox>
                                {cat.sources.length === 0 ? (
                                    <span style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>
                                        Nenhuma fonte configurada. Adicione fontes de listas para esta categoria.
                                    </span>
                                ) : (
                                    <VBox style={{ gap: '8px' }}>
                                        {cat.sources.map((src) => (
                                            <SourceRow
                                                key={src.id}
                                                source={src}
                                                onToggle={(active) => mutToggleSource.mutate({ sourceId: src.id, active })}
                                                onDelete={() => mutDeleteSource.mutate(src.id)}
                                            />
                                        ))}
                                    </VBox>
                                )}
                            </VBox>
                        );
                    })()}
                </VBox>
            )}

            {/* Modal: adicionar fonte */}
            <Modal
                show={isAddSourceOpen}
                title="Adicionar Fonte"
                onClose={() => setIsAddSourceOpen(false)}
                onCancel={() => setIsAddSourceOpen(false)}
                onConfirm={() =>
                    addSourceCatId &&
                    newSourceName.trim() &&
                    newSourceUrl.trim() &&
                    mutAddSource.mutate({ catId: addSourceCatId, name: newSourceName.trim(), url: newSourceUrl.trim() })
                }
                confirmProps={{
                    label: 'Adicionar',
                    disabled: mutAddSource.isLoading || !newSourceName.trim() || !newSourceUrl.trim(),
                }}
                cancelProps={{ label: 'Cancelar' }}
                size="sm"
            >
                <VBox style={{ gap: '12px' }}>
                    <VBox>
                        <label style={{ fontWeight: 500, marginBottom: 4 }}>Nome da Fonte</label>
                        <input
                            className="form-control"
                            value={newSourceName}
                            onChange={(e) => setNewSourceName(e.target.value)}
                            placeholder="Ex: StevenBlack Hosts"
                            autoFocus
                        />
                    </VBox>
                    <VBox>
                        <label style={{ fontWeight: 500, marginBottom: 4 }}>URL da Lista</label>
                        <input
                            className="form-control"
                            value={newSourceUrl}
                            onChange={(e) => setNewSourceUrl(e.target.value)}
                            placeholder="https://raw.githubusercontent.com/..."
                        />
                        <span style={{ fontSize: '11px', color: 'var(--color-muted)', marginTop: 4 }}>
                            URL de uma lista de hosts/domínios (formato: hosts file, adblock ou uma entrada por linha)
                        </span>
                    </VBox>
                </VBox>
            </Modal>
        </VBox>
    );
}

function SourceRow({
    source,
    onToggle,
    onDelete,
}: {
    source: CategorySourceDTO;
    onToggle: (active: boolean) => void;
    onDelete: () => void;
}) {
    return (
        <HBox
            style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-1)',
                borderRadius: '4px',
                padding: '8px 12px',
                alignItems: 'center',
                gap: '12px',
            }}
        >
            <input
                type="checkbox"
                checked={source.active}
                onChange={(e) => onToggle(e.target.checked)}
                title={source.active ? 'Desativar fonte' : 'Ativar fonte'}
            />
            <VBox style={{ flex: 1, minWidth: 0 }}>
                <HBox style={{ gap: '8px', alignItems: 'center', opacity: source.active ? 1 : 0.5 }}>
                    <strong>{source.name}</strong>
                    {source.global && <Badge variant="info">Sistema</Badge>}
                </HBox>
                <span
                    style={{
                        fontSize: '12px',
                        color: 'var(--color-muted)',
                        wordBreak: 'break-all',
                        opacity: source.active ? 1 : 0.5,
                    }}
                >
                    {source.url}
                </span>
            </VBox>
            {/* Lista do sistema: compartilhada entre clientes, só liga/desliga. */}
            {!source.global && (
                <BotaoPerigoso
                    size="sm"
                    variant="outline-danger"
                    icon={mdiTrashCan}
                    onConfirm={onDelete}
                />
            )}
        </HBox>
    );
}
